import type { PolymorphicRef } from '@sds-system/types';
import type { ElementType, MouseEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { Icon } from '..';
import {
  StyledChipEndIconWrapper,
  StyledChipRoot,
  StyledChipStartIconWrapper,
  StyledChipTextWrapper,
} from './Chip.styled';
import type { ChipComponent, ChipProps } from './Chip.types';

export const Chip: ChipComponent = forwardRef(
  <C extends ElementType = 'div'>(props: ChipProps<C>, ref: PolymorphicRef<C>) => {
    const { styledComponents, text, onClickIcon, onClick, onChangeSelected, ...restProps } = props;
    const {
      endIconSource,
      startIconSource,
      disabled,
      variant = 'solid',
      size = 'S',
      selected,
      selectable,
      activeStyles,
    } = restProps;
    const [isSelected, setIsSelected] = useState(selected || false);

    const handleClickChip = (e: MouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented) return;
      if (selectable) setIsSelected((p) => !p);
      onClick?.();
    };
    const handleClickIcon = (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onClickIcon?.();
    };

    useEffect(() => {
      if (!selectable) return;
      onChangeSelected?.(isSelected);
    }, [isSelected]);

    useEffect(() => {
      if (!selectable || typeof selected === 'undefined') return;
      setIsSelected(selected);
    }, [selected]);

    return (
      <StyledChipRoot
        {...restProps}
        ref={ref}
        {...(!disabled && { onClick: handleClickChip })}
        {...(selectable && { selected: isSelected })}
        activeStyles={activeStyles}
        hasIcon={!!endIconSource || !!startIconSource}
        isClickable={!disabled && !!onClick}
        size={size}
        variant={variant}
        {...(styledComponents?.root && styledComponents.root)}
      >
        {startIconSource && (
          <StyledChipStartIconWrapper
            variant={variant}
            {...(styledComponents?.startIcon && styledComponents.startIcon)}
          >
            {typeof startIconSource === 'function' ? (
              <Icon size={styledComponents?.startIcon?.size || '3XS'} source={startIconSource} />
            ) : (
              startIconSource
            )}
          </StyledChipStartIconWrapper>
        )}

        <StyledChipTextWrapper {...(styledComponents?.text && styledComponents.text)}>
          {text}
        </StyledChipTextWrapper>

        {endIconSource && (
          <StyledChipEndIconWrapper
            disabled={disabled}
            variant={variant}
            {...(!disabled && { onClick: handleClickIcon })}
            isClickable={!disabled && !!onClickIcon}
            {...(styledComponents?.endIcon && styledComponents.endIcon)}
          >
            <Icon size={styledComponents?.endIcon?.size || '3XS'} source={endIconSource} />
          </StyledChipEndIconWrapper>
        )}
      </StyledChipRoot>
    );
  },
);
